/**
 * Reminders for calendar events that are about to start.
 *
 * The rule: an event is due a reminder once it is within fifteen minutes of
 * starting, unless it has been dismissed, or snoozed to a nearer lead time
 * that has not arrived yet. Arriving on the dashboard with fourteen minutes to
 * go therefore raises the reminder immediately — the trigger is the state of
 * the clock, not an event that fires once and is missed if nobody was looking.
 *
 * Decisions are stored in localStorage, keyed on the ICS uid plus the start
 * time. NOT on the row id: the cache is deleted and rebuilt on every refresh,
 * so ids do not survive a page load, and a dismissed reminder would come back
 * the moment the feed was re-fetched. uid alone is not enough either — every
 * occurrence of a recurring series shares one, so dismissing this Monday's
 * lecture would dismiss every Monday's.
 *
 * Per-browser rather than per-account, like the other display preferences
 * here. Dismissing a reminder on a laptop leaves it live on a phone, which is
 * the behaviour you want from something whose job is to interrupt you where
 * you are.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import type { CalendarEvent } from '@/api/types'
import { parseTimestamp } from '@/lib/format'

const STORAGE_KEY = 'docket.calendarReminders'

/** How early a reminder first appears. */
export const LEAD_SECONDS = 15 * 60

/** Offered as snooze targets, in seconds before the event starts. Only those
 *  still in the future are shown, so a meeting four minutes away does not
 *  offer to remind you in ten. */
export const SNOOZE_CHOICES = [10 * 60, 5 * 60, 0]

/** A reminder stops once the event has actually started. The minute of grace
 *  is what lets the "0 minutes before" choice fire at all: without it the
 *  window would close in the same instant it opened. */
const GRACE_SECONDS = 60

/** Decisions are forgotten a day after the event, so the store cannot grow
 *  without bound on a busy calendar. */
const PRUNE_AFTER_SECONDS = 24 * 60 * 60

type Decision =
  | { mode: 'dismissed' }
  /** Do not show again until this many seconds before the start. */
  | { mode: 'snoozed'; leadSeconds: number }

type Store = Record<string, Decision & { startsAt: string }>

/** Identity that survives a refresh. Falls back to the summary when a feed
 *  omits UID, which is malformed but not worth crashing over. */
export function reminderKey(event: CalendarEvent): string {
  return `${event.uid ?? event.summary ?? 'untitled'}|${event.starts_at}`
}

function read(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Store) : {}
  } catch {
    // A corrupt or unavailable store must not stop the dashboard rendering.
    return {}
  }
}

function write(store: Store): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch {
    /* Private mode, quota, or no storage at all. Reminders still work for
       this page view; they just will not be remembered. */
  }
}

function prune(store: Store, now: number): Store {
  const out: Store = {}
  for (const [key, value] of Object.entries(store)) {
    const startsAt = parseTimestamp(value.startsAt).getTime()
    if (now - startsAt < PRUNE_AFTER_SECONDS * 1000) out[key] = value
  }
  return out
}

/** Seconds until this event starts. Negative once it has begun. */
export function secondsUntil(event: CalendarEvent, now: number): number {
  return (parseTimestamp(event.starts_at).getTime() - now) / 1000
}

/**
 * Should this event be showing a reminder right now?
 *
 * Pure, and the whole rule in one place: within the lead time, not yet
 * started, not dismissed, and past any snooze.
 */
export function isDue(
  event: CalendarEvent,
  decision: Decision | undefined,
  now: number,
): boolean {
  if (event.is_all_day) return false // no time of day to be early for
  if (decision?.mode === 'dismissed') return false

  const lead = secondsUntil(event, now)
  if (lead < -GRACE_SECONDS) return false // already under way

  const threshold = decision?.mode === 'snoozed' ? decision.leadSeconds : LEAD_SECONDS
  return lead <= threshold
}

export function useEventReminders(events: CalendarEvent[], now: number) {
  const [store, setStore] = useState<Store>(() => prune(read(), Date.now()))

  // Another tab dismissing a reminder should settle it here too, rather than
  // leaving the same dialog up on two screens.
  useEffect(() => {
    function onStorage(change: StorageEvent) {
      if (change.key === STORAGE_KEY) setStore(prune(read(), Date.now()))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const decide = useCallback((event: CalendarEvent, decision: Decision) => {
    setStore((current) => {
      const next = prune(
        { ...current, [reminderKey(event)]: { ...decision, startsAt: event.starts_at } },
        Date.now(),
      )
      write(next)
      return next
    })
  }, [])

  const dismiss = useCallback(
    (event: CalendarEvent) => decide(event, { mode: 'dismissed' }),
    [decide],
  )
  const snooze = useCallback(
    (event: CalendarEvent, leadSeconds: number) =>
      decide(event, { mode: 'snoozed', leadSeconds }),
    [decide],
  )

  /** Everything due right now, soonest first. */
  const due = useMemo(
    () =>
      events
        .filter((event) => isDue(event, store[reminderKey(event)], now))
        .sort((a, b) => secondsUntil(a, now) - secondsUntil(b, now)),
    [events, store, now],
  )

  return { due, dismiss, snooze }
}
